import React from 'react';
import { UserRole } from '../types';
import {
  LayoutDashboard,
  Users,
  Calendar,
  FileText,
  Anchor,
  FileCheck,
  UserCheck,
  Settings,
  PlusCircle,
  ShieldCheck,
  Radio,
  Smartphone,
  UserPlus,
  Crown,
  Activity,
  ClipboardCheck,
  Banknote
} from 'lucide-react';
import { IconVariant } from './IconBadge';
import { playPulseSearchBeacon } from '../utils/pulseAudio';
import { playOwnerBloomNavSound } from '../utils/bloomOpenSound';
import { getRoleAccentStyles } from '../utils/roleAccent';

interface SidebarNavIconChipProps {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  variant?: IconVariant;
  active?: boolean;
  strokeWidth?: number;
  className?: string;
  isClinicalTheme?: boolean;
}

const SidebarNavIconChip: React.FC<SidebarNavIconChipProps> = ({
  icon: Icon,
  variant: _variant = 'sky',
  active = false,
  strokeWidth = 2,
  className = '',
  isClinicalTheme: _isClinicalTheme = true
}) => {
  return (
    <div
      className={`relative w-6 h-6 rounded-lg flex items-center justify-center shrink-0 transition-all duration-150 overflow-hidden border ${
        active
          ? 'bg-slate-800 border-slate-700 shadow-xs'
          : 'bg-white/80 border-slate-300/80 group-hover:border-slate-400 group-hover:bg-white shadow-none'
      } ${className}`}
    >
      <Icon
        className={`w-3.5 h-3.5 shrink-0 transition-colors duration-150 ${
          active
            ? 'text-white'
            : 'text-slate-600 group-hover:text-slate-900'
        }`}
        strokeWidth={strokeWidth}
      />
    </div>
  );
};

interface SidebarNavItemProps {
  id?: string;
  onClick: () => void;
  active: boolean;
  icon?: any;
  variant?: IconVariant;
  customBadge?: React.ReactNode;
  label: string;
  strokeWidth?: number;
  isClinicalTheme?: boolean;
}

const SidebarNavItem: React.FC<SidebarNavItemProps> = ({
  id,
  onClick,
  active,
  icon,
  variant = 'sky',
  customBadge,
  label,
  strokeWidth = 2,
  isClinicalTheme: _isClinicalTheme = true
}) => {
  const activeClasses = 'bg-white border-slate-300 text-slate-950 font-semibold shadow-xs';

  return (
    <button
      id={id}
      onClick={onClick}
      className={`relative group overflow-hidden w-full flex items-center px-2.5 py-1.5 text-xs rounded-xl transition-colors duration-150 ease-out border cursor-pointer ${
        active
          ? activeClasses
          : 'text-slate-700 hover:text-slate-950 border-transparent hover:bg-slate-200/60'
      }`}
    >
      {customBadge ? (
        customBadge
      ) : icon ? (
        <SidebarNavIconChip
          icon={icon}
          variant={variant}
          active={active}
          strokeWidth={strokeWidth}
          className="mr-2.5"
          isClinicalTheme={true}
        />
      ) : null}

      <span className={`tracking-wide transition-colors duration-150 ${
        active
          ? 'text-slate-950 font-semibold'
          : 'text-slate-700 group-hover:text-slate-950'
      }`}>
        {label}
      </span>
    </button>
  );
};

interface SidebarProps {
  currentTab: string;
  onSelectTab: (tab: string) => void;
  userRole: UserRole;
  onOpenNewSession: () => void;
  onOpenNewClient: () => void;
  onOpenIntakePacket?: () => void;
  onOpenAI?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  currentTab,
  onSelectTab,
  userRole,
  onOpenNewSession,
  onOpenNewClient,
  onOpenIntakePacket,
  onOpenAI
}) => {
  const isOwner = userRole === 'OWNER' || userRole === 'ADMIN';
  const selectNavTab = (tab: string) => {
    playOwnerBloomNavSound({ role: userRole });
    onSelectTab(tab);
  };
  const isReferralCoordinator = userRole === 'REFERRAL_COORDINATOR' || userRole === 'OFFICE';
  const isSupervisor = userRole === 'SUPERVISOR';
  const isAssistant = userRole === 'ASSISTANT';
  const isClinical = isSupervisor || isAssistant;

  // Action Buttons
  const canIntakeClient = isOwner || isReferralCoordinator;
  const hasTopAction = canIntakeClient;

  // Nav Visibility
  const canViewDashboard = isOwner || isReferralCoordinator;
  const canViewAssignments = isOwner || isSupervisor || isAssistant;
  const canViewNoteApproval = isSupervisor;
  const canViewClinicalDocs = isOwner || isSupervisor || isAssistant || isReferralCoordinator; // Clinical Documents
  const canViewMobileSoap = isSupervisor || isAssistant;
  const canViewSupervision = isOwner || isReferralCoordinator;
  const canViewMyPay = isSupervisor || isAssistant;
  const canViewAuthImport = isOwner;
  const canViewFinancials = isOwner || userRole === 'BILLING';
  const canViewAdmin = isOwner;

  const accentStyles = getRoleAccentStyles(userRole);
  const isMobileNavRole = isAssistant || isSupervisor || isReferralCoordinator || isOwner;

  return (
    <aside className={`relative ${isMobileNavRole ? 'hidden md:flex' : 'flex'} w-60 flex-col ${hasTopAction ? 'pt-4' : 'pt-2'} select-none bg-[#f0f4f8] border-r border-slate-200/90 shadow-none text-slate-800 ${
      (isOwner || isReferralCoordinator) ? 'h-full md:h-[calc(100vh-3.5rem)]' : 'min-h-[calc(100vh-3.5rem)]'
    }`}>
      {/* Action Buttons — Strictly role-gated */}
      {hasTopAction && (
        <div className="px-3 mb-4 space-y-2">
          {/* Intake Packet / Intake — Scoped to Referral Coordinator */}
          {isReferralCoordinator && onOpenIntakePacket && (
            <button
              onClick={onOpenIntakePacket}
              id="sidebar-intake-packet-cta"
              className="relative w-full h-9 px-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white border border-emerald-700 shadow-xs hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99] text-xs font-semibold transition-all flex items-center justify-center cursor-pointer"
              title="Full Allegiant Intake Packet & Service Agreement Workflow"
            >
              <div className="flex items-center justify-center">
                <FileText className="w-4 h-4 text-emerald-100 shrink-0 mr-2" />
                <span className="tracking-wide font-semibold">Intake</span>
              </div>
            </button>
          )}

          {/* Intake — Owner Desktop Action (Opens same canonical Intake workflow) */}
          {isOwner && onOpenIntakePacket && (
            <button
              onClick={onOpenIntakePacket}
              id="sidebar-owner-intake-cta"
              className="relative w-full h-9 px-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white border border-emerald-700 shadow-xs hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99] text-xs font-semibold transition-all flex items-center justify-center cursor-pointer"
              title="Intake (Full Packet & Service Agreement)"
            >
              <div className="flex items-center justify-center">
                <FileText className="w-4 h-4 text-emerald-100 shrink-0 mr-2" />
                <span className="tracking-wide font-semibold">Intake</span>
              </div>
            </button>
          )}

          {/* Client — Scoped to Operational / Management Roles (Owner / Referral Coordinator) */}
          {canIntakeClient && (
            <button
              onClick={onOpenNewClient}
              id="sidebar-new-client-cta"
              className="relative w-full h-9 px-2.5 rounded-xl bg-white hover:bg-slate-50 text-slate-800 hover:text-slate-950 border border-slate-300 shadow-xs hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99] text-xs font-semibold transition-all duration-150 flex items-center justify-center cursor-pointer"
              title="Add New Client"
            >
              <div className="flex items-center justify-center">
                <UserPlus className="w-4 h-4 text-slate-600 group-hover:text-slate-900 mr-2 shrink-0" />
                <span className="tracking-wide">Client</span>
              </div>
            </button>
          )}

          {/* Pulse Intelligence (AI Orb) — Scoped to Owner and Referral Coordinator desktop sidebar */}
          {(isOwner || isReferralCoordinator) && onOpenAI && (
            <button
              type="button"
              onClick={() => {
                playPulseSearchBeacon();
                onOpenAI();
              }}
              id="sidebar-ai-assist-cta"
              data-ai-assist-trigger="true"
              className="relative w-full h-9 rounded-xl bg-[#162030] hover:bg-[#1f2d42] border border-slate-700 text-white shadow-xs hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99] transition-all flex items-center justify-center cursor-pointer"
              title="Pulse Intelligence"
              aria-label="Pulse Intelligence"
            >
              <div className="flex items-center justify-center shrink-0">
                <img
                  src="/brand/canonical/bloom-mark-micro.svg"
                  alt=""
                  width={22}
                  height={22}
                  className="w-[22px] h-[22px] shrink-0 object-contain select-none pointer-events-none"
                />
              </div>
            </button>
          )}
        </div>
      )}

      {/* Nav List */}
      <div className="space-y-1 flex-1 overflow-y-auto px-2">
        {/* ========================================================================= */}
        {/* SUPERVISOR NAVIGATION                                                     */}
        {/* ========================================================================= */}
        {isSupervisor && (
          <>
            <div className="px-3 pt-1 pb-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
              Clinical
            </div>
            {/* Client Charts */}
            <SidebarNavItem
              onClick={() => selectNavTab('clients')}
              active={currentTab === 'clients'}
              icon={Users}
              variant="indigo"
              label="Client Charts"
              isClinicalTheme={true}
            />

            {/* Approvals */}
            <SidebarNavItem
              onClick={() => selectNavTab('notes')}
              active={currentTab === 'notes'}
              icon={FileCheck}
              variant="indigo"
              label="Approvals"
              strokeWidth={2.2}
              isClinicalTheme={true}
            />

            {/* Clinical Documents */}
            <SidebarNavItem
              onClick={() => selectNavTab('clinical')}
              active={currentTab === 'clinical'}
              icon={FileText}
              variant="indigo"
              label="Clinical Documents"
              isClinicalTheme={true}
            />

            {/* Caseload */}
            <SidebarNavItem
              onClick={() => selectNavTab('assignments')}
              active={currentTab === 'assignments' || currentTab === 'scheduling' || currentTab === 'visit-planner'}
              icon={UserCheck}
              variant="indigo"
              label="Caseload"
              isClinicalTheme={true}
            />

            {/* SOAP */}
            <SidebarNavItem
              onClick={() => selectNavTab('mobile-soap')}
              active={currentTab === 'mobile-soap'}
              icon={Smartphone}
              variant="slate"
              label="SOAP"
              isClinicalTheme={true}
            />

            {/* Earnings Heading */}
            <div className="px-3 py-1.5 mt-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
              Earnings
            </div>
            {/* My Pay */}
            <SidebarNavItem
              id="nav-my-pay"
              onClick={() => selectNavTab('my-pay')}
              active={currentTab === 'my-pay'}
              icon={Banknote}
              variant="emerald"
              label="My Pay"
              isClinicalTheme={true}
            />
          </>
        )}

        {/* ========================================================================= */}
        {/* ASSISTANT / SLPA NAVIGATION (Single Compact List: Client Charts, Planner, Docs, My Pay) */}
        {/* ========================================================================= */}
        {isAssistant && (
          <>
            <div className="px-3 pt-1 pb-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
              Clinical
            </div>
            {/* Client Charts */}
            <SidebarNavItem
              onClick={() => selectNavTab('clients')}
              active={currentTab === 'clients'}
              icon={Users}
              variant="cyan"
              label="Client Charts"
              isClinicalTheme={true}
            />

            {/* Planner */}
            <SidebarNavItem
              onClick={() => selectNavTab('assignments')}
              active={currentTab === 'assignments' || currentTab === 'scheduling' || currentTab === 'visit-planner'}
              icon={Calendar}
              variant="cyan"
              label="Planner"
              isClinicalTheme={true}
            />

            {/* Clinical Documents */}
            <SidebarNavItem
              onClick={() => selectNavTab('clinical')}
              active={currentTab === 'clinical'}
              icon={FileText}
              variant="cyan"
              label="Clinical Documents"
              isClinicalTheme={true}
            />

            {/* SOAP */}
            <SidebarNavItem
              onClick={() => selectNavTab('mobile-soap')}
              active={currentTab === 'mobile-soap'}
              icon={Smartphone}
              variant="slate"
              label="SOAP"
              isClinicalTheme={true}
            />

            {/* Earnings Heading */}
            <div className="px-3 py-1.5 mt-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
              Earnings
            </div>
            {/* My Pay */}
            <SidebarNavItem
              id="nav-my-pay"
              onClick={() => selectNavTab('my-pay')}
              active={currentTab === 'my-pay'}
              icon={Banknote}
              variant="emerald"
              label="My Pay"
              isClinicalTheme={true}
            />
          </>
        )}

        {/* ========================================================================= */}
        {/* REFERRAL COORDINATOR NAVIGATION (Single Continuous List, No Headings)     */}
        {/* ========================================================================= */}
        {isReferralCoordinator && (
          <>
            {/* Dashboard */}
            <SidebarNavItem
              onClick={() => selectNavTab('dashboard')}
              active={currentTab === 'dashboard'}
              icon={LayoutDashboard}
              variant="rose"
              label="Dashboard"
            />

            {/* Client Charts */}
            <SidebarNavItem
              onClick={() => selectNavTab('clients')}
              active={currentTab === 'clients'}
              icon={Users}
              variant="rose"
              label="Client Charts"
            />

            {/* Clinical Documents */}
            <SidebarNavItem
              onClick={() => selectNavTab('clinical')}
              active={currentTab === 'clinical'}
              icon={FileText}
              variant="rose"
              label="Clinical Documents"
            />

            {/* Providers */}
            <SidebarNavItem
              onClick={() => selectNavTab('supervision')}
              active={currentTab === 'supervision'}
              icon={UserCheck}
              variant="rose"
              label="Providers"
            />

            {/* Provider Activity */}
            <SidebarNavItem
              onClick={() => selectNavTab('provider-activity')}
              active={currentTab === 'provider-activity'}
              icon={Activity}
              variant="rose"
              label="Provider Activity"
            />

            <SidebarNavItem
              id="nav-rc-wellsky-import"
              onClick={() => selectNavTab('authorizations')}
              active={currentTab === 'authorizations'}
              icon={Anchor}
              variant="rose"
              label="WellSky Import"
            />

            {/* Planner */}
            <SidebarNavItem
              onClick={() => selectNavTab('assignments')}
              active={currentTab === 'assignments' || currentTab === 'scheduling' || currentTab === 'visit-planner'}
              icon={Calendar}
              variant="rose"
              label="Planner"
            />
          </>
        )}

        {/* ========================================================================= */}
        {/* OWNER / ADMIN NAVIGATION (Rich Multi-Section Shell - UNTOUCHED)           */}
        {/* ========================================================================= */}
        {isOwner && (
          <>
            <div className="px-3 py-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              Main
            </div>
            {canViewDashboard && (
              <SidebarNavItem
                onClick={() => selectNavTab('dashboard')}
                active={currentTab === 'dashboard'}
                icon={LayoutDashboard}
                variant="teal"
                label="Dashboard"
              />
            )}
            <SidebarNavItem
              onClick={() => selectNavTab('clients')}
              active={currentTab === 'clients'}
              icon={Users}
              variant="teal"
              label="Client Charts"
            />
            {canViewAssignments && (
              <SidebarNavItem
                onClick={() => selectNavTab('assignments')}
                active={currentTab === 'assignments' || currentTab === 'scheduling' || currentTab === 'visit-planner'}
                icon={Calendar}
                variant="teal"
                label="Planner"
              />
            )}

            {/* Clinical Section */}
            <div className="px-3 py-1.5 mt-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              Clinical
            </div>
            {canViewClinicalDocs && (
              <SidebarNavItem
                onClick={() => selectNavTab('clinical')}
                active={currentTab === 'clinical'}
                icon={FileText}
                variant="cyan"
                label="Clinical Documents"
              />
            )}
            {canViewSupervision && (
              <>
                <SidebarNavItem
                  onClick={() => selectNavTab('supervision')}
                  active={currentTab === 'supervision'}
                  icon={UserCheck}
                  variant="cyan"
                  label="Providers"
                />
                <SidebarNavItem
                  onClick={() => selectNavTab('provider-activity')}
                  active={currentTab === 'provider-activity'}
                  icon={Activity}
                  variant="cyan"
                  label="Provider Activity"
                />
              </>
            )}
            {canViewAuthImport && (
              <SidebarNavItem
                onClick={() => selectNavTab('authorizations')}
                active={currentTab === 'authorizations'}
                icon={Anchor}
                variant="cyan"
                label="WellSky Import"
              />
            )}

            {/* Revenue Section - Strictly OWNER / Financial Administration */}
            {canViewFinancials && (
              <>
                <div className="px-3 py-1.5 mt-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  Revenue & Claims
                </div>
                <SidebarNavItem
                  onClick={() => selectNavTab('billing')}
                  active={currentTab === 'billing'}
                  icon={ClipboardCheck}
                  variant="emerald"
                  label="Billing Readiness"
                />
                <SidebarNavItem
                  onClick={() => selectNavTab('claims')}
                  active={currentTab === 'claims'}
                  icon={FileCheck}
                  variant="emerald"
                  label="Claims & 837 EDI"
                />
                <SidebarNavItem
                  onClick={() => selectNavTab('payroll')}
                  active={currentTab === 'payroll'}
                  icon={Banknote}
                  variant="emerald"
                  label="Payroll"
                />
              </>
            )}

            {/* Administration Section - Strictly OWNER / Admin */}
            {canViewAdmin && (
              <>
                <div className="px-3 py-1.5 mt-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                  Administration
                </div>
                <SidebarNavItem
                  onClick={() => selectNavTab('connections')}
                  active={currentTab === 'connections'}
                  icon={Radio}
                  variant="slate"
                  label="Connections"
                />
                <SidebarNavItem
                  onClick={() => selectNavTab('admin')}
                  active={currentTab === 'admin' || currentTab === 'ai-usage'}
                  icon={Settings}
                  variant="slate"
                  label="Administration"
                />
              </>
            )}
          </>
        )}
      </div>

      {/* Footer Info — Continuous with rail background */}
      <div className={`p-3.5 border-t border-slate-200/90 text-[10px] flex flex-col space-y-1 bg-transparent text-slate-500 ${
        (isOwner || isReferralCoordinator) ? 'md:pb-14' : ''
      }`}>
        <div className="flex justify-between items-center">
          <span className="text-slate-500">Database</span>
          <span className="text-emerald-700 font-mono font-medium">Cloud SQL Live</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-slate-500">Pulse Intelligence</span>
          <span className="text-slate-600 font-mono font-medium">Ready</span>
        </div>
      </div>
    </aside>
  );
};
